import { Component, ElementRef, ViewChild } from '@angular/core';
import { ClxFormComponent } from '@forms';
import { ClxInputComponent } from '@forms/clx-forms/controls/clx.input.component';
import { EditorView, basicSetup } from 'codemirror'
import { keymap, ViewPlugin, Decoration, DecorationSet, MatchDecorator, ViewUpdate, highlightActiveLine } from '@codemirror/view';
import { EditorState, Compartment, StateField, StateEffect, Range } from "@codemirror/state"
import { StreamLanguage } from "@codemirror/language"
import { lua } from "@codemirror/legacy-modes/mode/lua"
import { indentWithTab } from "@codemirror/commands"

import { CodeResponseMessage, ScriptingEditorService } from './scripting-editor.service';


import { ButtonStates } from '@forms/clx-forms/controls/clx.button.component';
import { CodeSnippet } from './code-snippets/code-snippet.interface';
import { ClxTextareaComponent } from '@forms/clx-forms/controls/clx.textarea.component';
import { AddWhileLoopSnippet } from './code-snippets/add-while-loop.snippet';
import { AddDatapointSnippet } from './code-snippets/add-datapoint.snippet';
import { AddMainLoopSnippet } from './code-snippets/add-main-loop.snippet';
import { AddSystemMainLoopSnippet } from './code-snippets/add-main-system-loop.snippet';
import { AddAlarmMainLoopSnippet } from './code-snippets/add-main-alarm-loop.snippet';
import { ProcessAlarmsSnippet } from './code-snippets/process-alarms.snippet';
import { ReadDatapointSnippet } from './code-snippets/read-datapoint.snippet';
import { WriteDatapointSnippet } from './code-snippets/write-datapoint.snippet';
import { ReadAndWriteDatapointExample } from './code-examples/read-and-write-datapoint.example';
import { AddForLoopSnippet } from './code-snippets/add-for-loop.snippet';
import { IfElseStatementpSnippet } from './code-snippets/if-else-statement.snippet';
import { IfElseIfStatementpSnippet } from './code-snippets/if-else-if-else-statement.snippet';
import { ShowSnippet } from './code-snippets/show.snippet';
import { ClxCodeGridComponent } from '@forms/clx-forms';
import { StoreLocal } from '@database';
import { PrivilegesService } from '@privileges';
import { RoutePrivilegeNames } from '@route-privilege-names';
import { Config } from '@config';
import { AddRootCausesSnippet } from './code-snippets/add-root-cause.snippet';
import { AddAlarmTypesSnippet } from './code-snippets/add-alarm-type.snippet';
import { ActOnPlantSnippet } from './code-snippets/act-on-plant.snippet';
import { AddUntilLoopSnippet } from './code-snippets/add-until-loop.snippet';
import { ScriptMode } from './enums';
import { ClxSelectComponent } from '../forms-module/clx-forms/controls/clx.select.component';

import { ReadAndWriteDatapointExampleOOP } from './code-examples/read-and-write-datapoint.oop.example';
import { ReadAndWriteDatapointExampleSystem } from './code-examples/read-and-write-datapoint.example.system';
import { ReadAndWriteDatapointExampleAlarm } from './code-examples/process-alarms.example';
import { NorthboundApiExampleDPHistory } from './code-examples/call-northbound-api.example';
import { CallNBApi } from './code-snippets/call.nb.api';
import { EquivalenciesExample } from './code-examples/read-and-write-datapoint.equivalencies.example';
import { IterateArraySnippet } from './code-snippets/iterate.array.snippet';
import { IterateObjectSnippet } from './code-snippets/iterate.object.snippet';
import { ShowFrmtSnippet } from './code-snippets/show.frmt.snippet';
import { CustomFunctionExample } from './code-examples/functions.example';
import { MiscFunctionExample } from './code-examples/misc-functions.example';


@Component({
    selector: 'scripting-editor',
    templateUrl: './scripting-editor.template.html',
    standalone: false
})
export class ScriptingEditorComponent {
    constructor(
        private service: ScriptingEditorService,
        private privilegeService: PrivilegesService,
        private config: Config
    ){
        const privileges = this.privilegeService.getPrivileges();
        this.isSystemAdministrator = privileges.find(p => p.name === RoutePrivilegeNames.SystemAdministrator) !== undefined;
       // this.allowAlarmAndSystemSelection = this.config.serverConfig.DEPLOYMENT === '' || this.config.serverConfig.DEPLOYMENT === 'LOCAL' || this.config.serverConfig.DEPLOYMENT === 'alpha'

        this.snippets.push(AddMainLoopSnippet);
        this.snippets.push(AddSystemMainLoopSnippet);
        this.snippets.push(AddAlarmMainLoopSnippet);
        this.snippets.push(ProcessAlarmsSnippet);
        this.snippets.push(AddAlarmTypesSnippet);
        this.snippets.push(AddRootCausesSnippet);
        this.snippets.push(AddForLoopSnippet);
        this.snippets.push(IterateArraySnippet);
        this.snippets.push(IterateObjectSnippet);
        this.snippets.push(AddWhileLoopSnippet);
        this.snippets.push(AddUntilLoopSnippet);
        this.snippets.push(IfElseStatementpSnippet);
        this.snippets.push(IfElseIfStatementpSnippet);
        this.snippets.push(ActOnPlantSnippet);
        this.snippets.push(ReadDatapointSnippet);
        this.snippets.push(WriteDatapointSnippet);
        this.snippets.push(AddDatapointSnippet);
        this.snippets.push(ShowSnippet);
        this.snippets.push(ShowFrmtSnippet);
        this.snippets.push(CallNBApi);

        this.examples.push(ReadAndWriteDatapointExample);
        this.examples.push(ReadAndWriteDatapointExampleOOP);
        this.examples.push(EquivalenciesExample);
        this.examples.push(ReadAndWriteDatapointExampleSystem);
        this.examples.push(ReadAndWriteDatapointExampleAlarm);
        this.examples.push(NorthboundApiExampleDPHistory);
        this.examples.push(CustomFunctionExample);
        this.examples.push(MiscFunctionExample);
    }

    @ViewChild(ClxFormComponent)
    public form: ClxFormComponent;

    @ViewChild('plantIdInput')
    public plantIdInput: ClxInputComponent;

    @ViewChild(ClxTextareaComponent)
    public editor: ClxTextareaComponent;

    @ViewChild(ClxCodeGridComponent)
    public codeGrid: ClxCodeGridComponent;

    public isPlantSelectionOpen: boolean = false;

    @ViewChild('scriptType')
    public scriptType: ClxSelectComponent;

    public buttonState: ButtonStates = null;

    public messages: CodeResponseMessage[] = [];

    public liveCodeingEnabled: boolean = false;

    public isSystemAdministrator: boolean = false;

    public allowAlarmAndSystemSelection: boolean = true;

    public addComments: boolean = true;

    public oopMode: boolean = false;

    public traceApiCalls: boolean = true;

    @StoreLocal()
    public plantId: string = '';

    @StoreLocal()
    public script: string = '';

    @StoreLocal()
    public systemId: string = ''

    public defaultDate = Date.now();

    public testAlarms: any = [{
        alarmId: '1337',
        alarmString: '', 
        utcTime: this.defaultDate,
        eventState : 0 
    }]

    public async execute(formData: any){
        this.messages = [];
        this.buttonState = ButtonStates.loading;
        const values = this.form.getControlValues();
        values.plantScript = this.getEditorValue();
        const data = await this.service.executeScript({...values});
        this.handleErrorMessages(data);
        this.buttonState = null;
        this.messages = data;
    }

    private handleErrorMessages(data) {
        let marktLineNumbers = []
        data.forEach((message) => {
            if(message.type === 'error') {

                // if error lines are given... use them
                if (message.lines.length > 0) {
                    marktLineNumbers = marktLineNumbers.concat(message.lines);
                } else {
                    // otherwise try parse message 
                    const parts = message.message.split(':');
                    let lineNo = parts?.[3]
                    if (lineNo && lineNo > -1) {
                        marktLineNumbers.push(Number(lineNo))
                    }
                }
            }
        })
        this.highlightErrorLines(marktLineNumbers);        
    }

    public openPlantSelection(){
        this.isPlantSelectionOpen = true;
    }

    public selectPlant(plant){
        this.plantId = plant.plantId;
        this.isPlantSelectionOpen = false;
    }

    public selectSystem(system) {
        this.systemId = system.systemId;
        this.isPlantSelectionOpen = false;
    }

    public snippets: any[] = [];

    public examples: any[] = [];

    public async insertSnippet(snippet: CodeSnippet) {

        const values = this.form.getControlValues();

        snippet.data.oop = values.oopMode

        if (this.scriptType.value == "multiplePlantsScheduledAction") {
            snippet.data.mode = "S";
            snippet.data.plantInput = null;
            snippet.data.systemInput = this.plantIdInput;
        } else if (this.scriptType.value == "singlePlantScheduledAction") {
            snippet.data.mode = "P";
            snippet.data.plantInput = this.plantIdInput;
            snippet.data.systemInput = null;
        } else if (this.scriptType.value == "alarmAction") {
            snippet.data.mode = "A";
            snippet.data.plantInput = this.plantIdInput;
            snippet.data.systemInput = null;
        } 

        let value = snippet.insert();
        if(value instanceof Promise) {
            value = await value;
        }
        if (value == null || value == "") {
            return;
        }

        this.addComments = values.addComments;

        if (!this.addComments) {
            value = value.replace(/^\s*--.*$/gm, '');
        }

        let str = value;
        let indices = [];
        for (let i = 0; i < str.length; i++) {
            if (str[i] === "�") indices.push(i);
        }

        let postselectstart = -1;
        let postselectlen   = -1;

        if (indices.length == 2 && indices[0] >= 0 && indices[1] >= 0) {
            value = value.replace('�', '').replace('�', '');
            postselectstart = indices[0];
            postselectlen = indices[1] - indices[0];
        }

        this.insertSnippetIntoView(snippet, value, postselectstart, postselectlen) 
    }

    insertSnippetIntoView(snippet, value, postselectstart, postselectlen) {
        if(snippet.part) {
            const cursorStart = this.#currentView.state.selection.main.from;
            const cursorEnd   = this.#currentView.state.selection.main.to;

            this.#currentView.dispatch({
                changes: {
                    from: cursorStart,
                    to: cursorEnd,
                    insert: value
                },
                selection: postselectstart >= 0
                    ? { anchor: cursorStart + postselectstart, head: cursorStart + postselectstart + postselectlen - 1 }
                    : { anchor: cursorStart }
             })

        }else{
            const len = this.#currentView.state.doc.length;

            this.#currentView.dispatch({
                changes: {
                    from: 0,
                    to: len,
                    insert: value
                },
                selection: postselectstart >= 0
                    ? { anchor: postselectstart, head: postselectstart + postselectlen - 1 }
                    : { anchor: 0 }
            });
        }

        this.#currentView.focus()
    }

    public downloadUcfFile(value) {
        const filename = 'ScriptingRuntime.ucf';
        const blob = this.service.createUcfFile(value, filename);
        const element = window.document.createElement('a');

        element.href = window.URL.createObjectURL(blob);
        element.download = filename;        
        document.body.appendChild(element);
        element.click();        
        document.body.removeChild(element);
    }


    public async showGlobals(){
        this.messages = [];
        let data: any = this.service.convertMessages([
            {
                "level": "Info",
                "message": "\r\nassert\r\ndump\r\nerror\r\ngetvalue\r\nipairs\r\nmath\r\n    abs\r\n    acos\r\n    asin\r\n    atan\r\n    ceil\r\n    cos\r\n    deg\r\n    exp\r\n    floor\r\n    fmod\r\n    huge\r\n    log\r\n    max\r\n    maxinteger\r\n    min\r\n    mininteger\r\n    modf\r\n    pi\r\n    rad\r\n    random\r\n    randomseed\r\n    sin\r\n    sqrt\r\n    tan\r\n    tointeger\r\n    type\r\n    ult\r\nnext\r\nos\r\n    clock\r\n    date\r\n    difftime\r\n    time\r\npairs\r\npcall\r\nselect\r\nsetvalue\r\nshow\r\nstring\r\n    byte\r\n    char\r\n    find\r\n    format\r\n    gmatch\r\n    gsub\r\n    len\r\n    lower\r\n    match\r\n    pack\r\n    packsize\r\n    rep\r\n    reverse\r\n    sub\r\n    unpack\r\n    upper\r\ntable\r\n    concat\r\n    insert\r\n    move\r\n    pack\r\n    remove\r\n    sort\r\n    unpack\r\ntonumber\r\ntostring\r\ntype\r\nutf8\r\n    char\r\n    charpattern\r\n    codepoint\r\n    codes\r\n    len\r\n    offset\r\nwarn\r\nxpcall"
            }
        ])
        data = this.service.generateMessageOutput(data);
        this.handleErrorMessages(data);
        this.buttonState = null;
        this.messages = data;
    }

    public async autoUpdate() {
        const values = this.form.getControlValues();
        const data = await this.service.executeScript({...values});
        this.handleErrorMessages(data);
        this.buttonState = null;
        this.messages = data;
    }

    public  updateInterval: any;

    public toggleLiveCoding() {
        if(this.liveCodeingEnabled){
            this.liveCodeingEnabled = false;
            clearInterval(this.updateInterval)
        }else{
            this.liveCodeingEnabled = true;
            this.autoUpdate();
            this.updateInterval = setInterval(
                this.autoUpdate.bind(this),
                5000
            )
        }
    }

    public ngOnDestroy() {
        clearInterval(this.updateInterval)
    }

    @ViewChild('container')
    container: ElementRef;

    #currentView: any;

    // code mirror effect that you will use to define the effect you want (the decoration)
    static highlight_effect: any; 
    static clear_effect: any; 
    highlight_decoration: any;
    clear_decoration: any;

    errorHighlighted: boolean = false;

    initEditor() {
        const extensions = [basicSetup];
       
        ScriptingEditorComponent.highlight_effect = StateEffect.define<Range<Decoration>>();
        ScriptingEditorComponent.clear_effect = StateEffect.define<Range<Decoration>>();
        // define a new field that will be attached to your view state as an extension, update will be called at each editor's change
        const highlight_extension = StateField.define<any>({
            create() { return Decoration.none },
            update(value, transaction) {
                value = value.map(transaction.changes)

                for (let effect of transaction.effects) {
                    if (effect.is(ScriptingEditorComponent.highlight_effect)) {
                        value = value.update({ add: effect.value })
                    }
                    else {
                        value = value.update({ filter: spec => spec.type != this.highlight_decoration })
                    }
                }

                return value
            },
            provide: f => EditorView.decorations.from(f)
        });

        // this is your decoration where you can define the change you want : a css class or directly css attributes
        this.highlight_decoration = Decoration.line({
            attributes: {style: "background-color: #fdd"}
            //class: 'red_back'
        });

        this.clear_decoration = Decoration.mark({});

        extensions.push(highlight_extension);

        extensions.push(basicSetup);
        
        extensions.push(StreamLanguage.define(lua))

        extensions.push(StreamLanguage.define(lua))

        extensions.push(keymap.of([indentWithTab]));

        const tabSize = new Compartment

        extensions.push(tabSize.of(EditorState.tabSize.of(4)));

        extensions.push(EditorView.updateListener.of((v: ViewUpdate) => {
            if (v.docChanged) {
                if(this.errorHighlighted) {
                    this.#currentView.dispatch({
                        effects: ScriptingEditorComponent.clear_effect.of([this.clear_decoration.range(0, 1)])
                    });
                    this.errorHighlighted = false;
                }
            }
        }));

        const startState = EditorState.create({
            doc: '',
            extensions
        })

        this.#currentView = new EditorView({
            state: startState,
            parent: this.container.nativeElement
        });

        this.#currentView.dispatch({
            effects: tabSize.reconfigure(EditorState.tabSize.of(4))
        })
    }

    highlightErrorLines(lines) {

        let linesunique = [];

        lines.forEach(l => {
            if (l > 0) {
                let f = false;
                linesunique.forEach(lu => {
                    if (lu == l) {
                        f = true;
                    }
                });
                if (f === false) {
                    linesunique.push(l);
                }
            }
        });

        this.errorHighlighted = true;

        //remove previous marks
        this.#currentView.dispatch({
            effects: ScriptingEditorComponent.clear_effect.of([this.clear_decoration.range(0, 1)])
        });

        // mark lines
        let marks = [];
        let atLeastOne = false;

        linesunique.forEach(line => {
            atLeastOne = true;
            const l = this.#currentView.state.doc.line(line).from;
            marks.push(this.highlight_decoration.range(l, l));
        });

        if (atLeastOne) {
            this.#currentView.dispatch({
                effects: ScriptingEditorComponent.highlight_effect.of(marks)
            });
        }
    }

    getEditorValue() {
        if(this.#currentView?.state?.doc) {
            return this.#currentView.state.doc.toString()
        }
        return '';
    }

    ngAfterViewInit() {
        this.initEditor()
    }

}
